// Lead log table — the raw call/form detail rows AA's own client reports
// always show (date, type, caller, source, phone, duration, answered) that
// our dashboard was missing entirely: aggregate leads only (totals,
// by_source, by_status donut) plus a dead "View lead log" link with no page
// behind it. Core theme tokens only, no chart library — Core design tokens
// only, self-contained: no imports from sibling client-analytics files, so
// this frame can be reviewed/dropped in on its own (same pattern as
// MultiEngineRankTable.tsx).
//
// Capped upstream at 200 most-recent rows per client (scripts/aa_backfill.py's
// "what-converts-leads" pull + aa_build_fixture.py's merge_lead_log) — AA's
// own report showed 2,531 rows for one client over the same ~3 month window,
// so this is a bounded recent-activity sample, not the full log. `totalLeads`
// (LeadsSection.total_leads, when known) drives the "Showing N of M" caption
// so that boundedness is stated, not implied.

// Deliberately duplicates types/clientAnalytics.ts's LeadLogRow rather than
// importing it (this file stays import-free from sibling client-analytics
// files, see header comment) -- keep every field in sync with that type by
// hand.
export interface LeadLogRow {
  date: string; // AA's own timestamp format, "YYYY-MM-DD HH:MM:SS"
  lead_id: string;
  lead_type: string; // "Phone Call" or "Web Form"
  source: string;
  contact?: string | null;
  phone?: string | null;
  duration_seconds?: number | null;
  answered?: boolean | null; // null = not applicable (e.g. a web form)
  status_label: string;
}

export interface LeadLogTableProps {
  rows: LeadLogRow[];
  /** LeadsSection.total_leads, when known — drives the "Showing N of M"
   * caption's denominator so the 200-row cap reads as a sample, not the
   * whole log. Falls back to rows.length when omitted or smaller than it. */
  totalLeads?: number | null;
  /** The window this log actually covers (e.g. "last 90 days"), appended to the
   * "Lead log" header. Passed only when the surrounding panel's own caption
   * describes a DIFFERENT window (30-day rolling hero) so the log's real ~90-day
   * bake window is never misread as the 30-day set. Omit/null = no suffix. */
  windowLabel?: string | null;
}

// 6 realistic demo rows: an answered call, a missed call, a voicemail, two
// anonymous web forms (no contact/phone/duration/status), and a long
// domain-style source to exercise the truncate+tooltip path.
export const demoLeadLog: LeadLogRow[] = [
  {
    date: "2026-08-07 14:01:58",
    lead_id: "251331404",
    lead_type: "Phone Call",
    source: "gmb",
    contact: "Wireless Caller",
    phone: "(386) 341-3231",
    duration_seconds: 347,
    answered: true,
    status_label: "answered",
  },
  {
    date: "2026-08-07 10:35:13",
    lead_id: "251268789",
    lead_type: "Phone Call",
    source: "google",
    contact: "Sean Mccabe",
    phone: "(714) 679-4604",
    duration_seconds: 0,
    answered: false,
    status_label: "missed",
  },
  {
    date: "2026-08-06 15:32:00",
    lead_id: "251147877",
    lead_type: "Phone Call",
    source: "gmb",
    contact: "Maple Hts    Oh",
    phone: "(216) 518-9885",
    duration_seconds: 41,
    answered: false,
    status_label: "voicemail",
  },
  {
    date: "2026-08-06 15:21:44",
    lead_id: "251145038",
    lead_type: "Web Form",
    source: "google",
    contact: null,
    phone: "(386) 523-6713",
    duration_seconds: null,
    answered: null,
    status_label: "",
  },
  {
    date: "2026-08-03 11:31:23",
    lead_id: "250391879",
    lead_type: "Web Form",
    source: "affiliate-referral-network-partner-aaafence.com",
    contact: null,
    phone: null,
    duration_seconds: null,
    answered: null,
    status_label: "",
  },
  {
    date: "2026-08-01 14:33:01",
    lead_id: "250167674",
    lead_type: "Web Form",
    source: "bing",
    contact: null,
    phone: null,
    duration_seconds: null,
    answered: null,
    status_label: "",
  },
];

// "2026-08-07 14:01:58" -> "Aug 7, 2:01 PM". AA's timestamps are already
// account-local, so no timezone math — just a compact re-format.
function formatLeadDate(raw: string): string {
  const [datePart, timePart] = raw.split(" ");
  if (!datePart) return raw;
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return raw;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  let label = `${months[m - 1]} ${d}`;
  if (timePart) {
    const [hh, mm] = timePart.split(":").map(Number);
    if (!Number.isNaN(hh) && !Number.isNaN(mm)) {
      const period = hh >= 12 ? "PM" : "AM";
      const hour12 = hh % 12 === 0 ? 12 : hh % 12;
      label += `, ${hour12}:${String(mm).padStart(2, "0")} ${period}`;
    }
  }
  return label;
}

function formatDuration(seconds?: number | null): string {
  if (!seconds || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function StatusPill({ row }: { row: LeadLogRow }) {
  if (row.answered === null || row.answered === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  const label = row.status_label || (row.answered ? "Answered" : "Missed");
  const tone = row.answered
    ? "bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]"
    : "bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))]";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide leading-none ${tone}`}
      title={label}
    >
      {label}
    </span>
  );
}

export function LeadLogTable({ rows, totalLeads, windowLabel }: LeadLogTableProps) {
  // Adaptive: this frame renders nothing when there's no per-lead detail —
  // callers should still render the aggregate leads panel on its own.
  if (!rows || rows.length === 0) return null;

  const shown = rows.length;
  const total = totalLeads && totalLeads > shown ? totalLeads : shown;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border bg-muted/40">
        <div className="text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
          Lead log{windowLabel ? ` · ${windowLabel}` : ""}
        </div>
        <div className="text-dash-context text-muted-foreground tabular whitespace-nowrap">
          Showing {shown.toLocaleString()} of {total.toLocaleString()}
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full border-collapse text-dash-subtitle">
          <thead className="sticky top-0 z-10 bg-muted">
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Date
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Type
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Contact
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Source
              </th>
              <th className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Phone
              </th>
              <th className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Duration
              </th>
              <th className="px-4 py-2.5 text-center text-dash-label font-semibold uppercase tracking-wide text-muted-foreground">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.lead_id || `${row.date}-${i}`}
                className={`border-b border-border last:border-0 transition-colors hover:bg-muted/60 ${
                  i % 2 === 1 ? "bg-muted/30" : "bg-card"
                }`}
              >
                <td className="px-4 py-2 whitespace-nowrap text-foreground">
                  {formatLeadDate(row.date)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                  {row.lead_type || "—"}
                </td>
                <td
                  className="px-4 py-2 max-w-[160px] truncate text-foreground"
                  title={row.contact ?? undefined}
                >
                  {row.contact ?? "—"}
                </td>
                <td
                  className="px-4 py-2 max-w-[160px] truncate text-muted-foreground"
                  title={row.source || undefined}
                >
                  {row.source || "—"}
                </td>
                <td className="px-4 py-2 whitespace-nowrap tabular text-muted-foreground">
                  {row.phone ?? "—"}
                </td>
                <td className="px-4 py-2 text-right tabular text-foreground whitespace-nowrap">
                  {formatDuration(row.duration_seconds)}
                </td>
                <td className="px-4 py-2 text-center">
                  <StatusPill row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
